<script lang="ts">
import type { VariantProps } from 'tailwind-variants';
import type { ThemeSlots } from '#layers/ui/types/tv';
import type { ListItem, ListItemProps, ListItemSlots } from './ListItem.vue';
import { tv } from 'tailwind-variants';

export const list = tv({
	slots: {
		root: 'overflow-y-auto',
		list: 'rounded-[inherit]',
	},
	variants: {
		disabled: {
			true: '',
		},
		variant: {
			outline: {
				root: 'rounded-md border',
			},
			ghost: '',
		},
	},
	defaultVariants: {
		variant: 'outline',
	},
});

type ListVariants = VariantProps<typeof list>;

export interface ListProps<T extends ListItem> extends Pick<Partial<ListItemProps<T>>, 'sortable' | 'labelKey' | 'descriptionKey' | 'allowDragOnlyOnHandle'> {
	class?: string;
	disabled?: boolean;
	selectable?: boolean;
	variant?: ListVariants['variant'];
	size?: ListItemProps<T>['size'];
	ui?: ThemeSlots<typeof list> & { item?: ListItemProps<T>['ui'] };
}

type SlotProps<T extends ListItem> = (props: { item: T; index: number }) => any;
type ItemSlots<T extends ListItem> = {
	[K in `item-${keyof ListItemSlots<T>}`]: ListItemSlots<T>[K extends `item-${infer R}` ? R : never];
};
export type ListSlots<T extends ListItem> = {
	item: SlotProps<T>;
} & ItemSlots<T>;

export interface ListEmits<T extends ListItem> {
	select: [item: T, event?: Event];
	reorder: [event: { item: T; oldIndex: number; newIndex: number; items: T[] }];
}
</script>

<script setup lang="ts" generic="T extends ListItem">
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/vue';
import { computed, shallowRef, useTemplateRef } from 'vue';

const {
	labelKey = 'label',
	descriptionKey = 'description',
	selectable,
	disabled,
	variant,
	class: className,
	ui: uiProp,
	sortable,
	allowDragOnlyOnHandle,
} = defineProps<ListProps<T>>();
const emit = defineEmits<ListEmits<T>>();
const slots = defineSlots<ListSlots<T>>();
const items = defineModel<readonly T[]>('items', { required: true });
const isSelectable = computed(() => selectable ?? false);

function handleDragEnd(event: Parameters<typeof move>[1]) {
	const { source, canceled } = event.operation;
	const snapshot = originalItemsSnapshot.value;
	originalItemsSnapshot.value = null;

	if (!source || canceled || !snapshot) {
		return;
	}

	// oldIndex: where the item was in the original order
	const oldIndex = snapshot.findIndex((item) => item.id === source.id);
	// newIndex: where the item is NOW in the mutated array (after drag-over reordering)
	const newIndex = items.value.findIndex((item) => item.id === source.id);

	if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
		emit('reorder', {
			item: snapshot[oldIndex]!,
			oldIndex,
			newIndex,
			items: snapshot,
		});
	}

	applyMove(event);
}

// `move` always returns a freshly cloned array (we pass it a clone, required by the
// `readonly T[]` model). Reassigning unconditionally would emit a new reference even on a
// no-op drag, triggering reference watchers in consumers (e.g. marking a form dirty). Only
// assign when the resulting order actually differs from the current one.
function applyMove(event: Parameters<typeof move>[1]) {
	const next = move([...items.value], event);
	const changed = next.length !== items.value.length || next.some((item, index) => item.id !== items.value[index]?.id);

	if (changed) {
		items.value = next;
	}
}

const listRef = useTemplateRef('listRef');

// Store original order when drag starts to compute correct indices after drag-over mutations
const originalItemsSnapshot = shallowRef<T[] | null>(null);

function handleDragStart() {
	originalItemsSnapshot.value = [...items.value];
}

function itemSlotName(name: string) {
	// Unsuffixed `item` is DListItem's default slot.
	return name.replace(/^item-?/, '') || 'default';
}

const itemProps = computed(() => ({ sortable, labelKey, descriptionKey, allowDragOnlyOnHandle }));

const ui = computed(() => list({
	disabled,
	variant,
}));

// TODO(DnDProvider): remove as any once this is resolved https://github.com/clauderic/dnd-kit/issues/1627
const modifiers = [
	RestrictToVerticalAxis,
	RestrictToElement.configure({
		element: () => listRef.value,
	}),
] as any[];

function isItemSelectable(item: T) {
	return isSelectable.value && item.selectable !== false;
}

function handleSelect(item: T, event?: Event) {
	if (disabled || !isItemSelectable(item)) return;
	emit('select', item, event);
}

function handleKeyboardSelect(item: T, event: KeyboardEvent) {
	if (!isItemSelectable(item)) return;
	if (event.target !== event.currentTarget) return;

	event.preventDefault();
	handleSelect(item, event);
}
</script>

<template>
	<DragDropProvider
		:modifiers
		@drag-start="handleDragStart"
		@drag-over="applyMove"
		@drag-end="handleDragEnd"
	>
		<div :class="ui.root({ class: [className, uiProp?.root] })" data-testid="list-root">
			<ul ref="listRef" :class="ui.list({ class: [uiProp?.list] })">
				<DListItem
					v-for="(item, index) in items"
					:key="item.id"
					v-bind="{ item, index, ...itemProps }"
					:sortable="item.sortable !== false && itemProps.sortable"
					:list-disabled="disabled"
					:selectable="isItemSelectable(item)"
					:size
					:ui="uiProp?.item"
					@click="(event?: Event) => handleSelect(item, event)"
					@keydown.enter="handleKeyboardSelect(item, $event)"
					@keydown.space="handleKeyboardSelect(item, $event)"
				>
					<template v-for="(_, name) in slots" #[itemSlotName(name)]="slotData: any">
						<slot :name v-bind="slotData" />
					</template>
				</DListItem>
			</ul>
		</div>
	</DragDropProvider>
</template>
